/**
 * ClientStatusTab — Client Case-Status Tracker (Intake → Status)
 *
 * Displays case status for clients in the unified portal.
 * Uses MetricCard, StatusBadge, and FinancialValue primitives.
 *
 * Design: CSS variables only, dark default, Daylight via data-theme.
 * Brand orange (#FF6600) for active, green (#22C55E) for completed.
 *
 * @see docs/plans/redesign-plan §5.7/§5.11
 */

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Clock, FileText, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { api } from '@/utils/api';
import { MetricCard } from '@/components/shared/MetricCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { BRAND_NAME } from '@/config/brand';

interface PortalStatusResponse {
  status: string;
  claimant_name: string;
  transactions_total: number;
  transactions_answered: number;
  documents_total: number;
  documents_uploaded: number;
  progress_percent: number;
}

interface StatusStep {
  id: string;
  label: string;
  description: string;
  completed: boolean;
  active: boolean;
}

export default function ClientStatusTab() {
  const { token: pathToken } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const token = pathToken ?? searchParams.get('token') ?? undefined;
  const [statusData, setStatusData] = useState<PortalStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      fetchStatus();
    }
  }, [token]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await api.get(`/api/v1/portal/${token}/status`, {
        showErrorToast: false,
      });
      setStatusData(result);
    } catch (err: any) {
      if (err.message?.includes('404')) {
        setError('This link is invalid or has expired.');
      } else if (err.message?.includes('410')) {
        setError('This link has expired.');
      } else {
        setError(err.message || 'Failed to load status');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusSteps = (): StatusStep[] => {
    if (!statusData) return [];

    const { progress_percent } = statusData;

    return [
      {
        id: 'intake',
        label: 'Case Intake',
        description: 'Initial case setup and document request',
        completed: progress_percent >= 20,
        active: progress_percent < 20,
      },
      {
        id: 'documents',
        label: 'Document Upload',
        description: 'Financial records, tax returns, bank statements',
        completed: statusData.documents_uploaded >= statusData.documents_total && statusData.documents_total > 0,
        active: progress_percent >= 20 && progress_percent < 60,
      },
      {
        id: 'review',
        label: 'Data Review',
        description: 'Our team reviews and validates your documents',
        completed: progress_percent >= 60,
        active: progress_percent >= 60 && progress_percent < 80,
      },
      {
        id: 'analysis',
        label: 'Analysis',
        description: 'Forensic analysis and calculations',
        completed: progress_percent >= 80,
        active: progress_percent >= 80 && progress_percent < 100,
      },
      {
        id: 'complete',
        label: 'Complete',
        description: 'Your case is ready for review',
        completed: progress_percent >= 100,
        active: progress_percent >= 100,
      },
    ];
  };

  const getStatusVariant = (status: string): Parameters<typeof StatusBadge>[0]['variant'] => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'complete':
      case 'done':
        return 'done';
      case 'active':
      case 'in_progress':
        return 'active';
      case 'pending':
        return 'pending';
      case 'blocked':
        return 'blocked';
      default:
        return 'neutral';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[var(--color-brand)]" />
          <p className="text-[var(--color-text-secondary)]">Loading your case status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8">
        <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-xl p-6 text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-[var(--color-brand)]" />
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-2">Unable to Load Status</h2>
          <p className="text-[var(--color-text-secondary)]">{error}</p>
        </div>
      </div>
    );
  }

  if (!statusData) {
    return (
      <div className="py-8">
        <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-xl p-6 text-center">
          <p className="text-[var(--color-text-secondary)]">No status information available.</p>
        </div>
      </div>
    );
  }

  const steps = getStatusSteps();
  const currentStatus = statusData.status || 'pending';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1
            className="text-2xl font-bold text-[var(--color-text-primary)]"
            style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
          >
            Case Status
          </h1>
          <p className="text-[var(--color-text-secondary)] mt-1">
            Track your {BRAND_NAME} case progress
          </p>
        </div>
        <StatusBadge
          text={currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)}
          variant={getStatusVariant(currentStatus)}
          size="md"
        />
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Overall Progress"
          value={`${statusData.progress_percent}%`}
          badge={
            statusData.progress_percent === 100
              ? { text: 'Complete', variant: 'done' }
              : { text: 'In Progress', variant: 'active' }
          }
          accent={statusData.progress_percent < 100}
        />
        <MetricCard
          label="Documents"
          value={`${statusData.documents_uploaded}/${statusData.documents_total}`}
          subtitle="Required uploads"
          badge={
            statusData.documents_uploaded >= statusData.documents_total
              ? { text: 'Done', variant: 'done' }
              : { text: 'Pending', variant: 'pending' }
          }
          accent={statusData.documents_uploaded < statusData.documents_total}
        />
        <MetricCard
          label="Transactions"
          value={`${statusData.transactions_answered}/${statusData.transactions_total}`}
          subtitle="Categorized items"
          badge={
            statusData.transactions_answered >= statusData.transactions_total && statusData.transactions_total > 0
              ? { text: 'Done', variant: 'done' }
              : statusData.transactions_total > 0
                ? { text: 'Pending', variant: 'pending' }
                : { text: 'N/A', variant: 'neutral' }
          }
          accent={statusData.transactions_answered < statusData.transactions_total}
        />
        <MetricCard
          label="Estimated Time"
          value={statusData.progress_percent >= 100 ? 'Complete' : '2-3 days'}
          subtitle={statusData.progress_percent >= 100 ? 'Case is ready' : 'After documents received'}
          badge={
            statusData.progress_percent >= 100
              ? { text: 'Done', variant: 'done' }
              : { text: 'Active', variant: 'active' }
          }
          accent={statusData.progress_percent < 100}
        />
      </div>

      {/* Timeline */}
      <div className="bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-xl p-6">
        <h2
          className="text-lg font-semibold text-[var(--color-text-primary)] mb-6"
          style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
        >
          Progress Timeline
        </h2>

        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[19px] top-0 bottom-0 w-[2px] bg-[var(--color-bg-elevated)]" />

          <div className="space-y-6">
            {steps.map((step, index) => (
              <div key={step.id} className="relative flex items-start gap-4">
                {/* Step indicator */}
                <div
                  className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                    step.completed
                      ? 'bg-[var(--color-done)] border-[var(--color-done)]'
                      : step.active
                        ? 'bg-[var(--color-brand)] border-[var(--color-brand)]'
                        : 'bg-[var(--color-bg-surface)] border-[var(--color-border)]'
                  }`}
                >
                  {step.completed ? (
                    <CheckCircle className="w-5 h-5 text-white" />
                  ) : step.active ? (
                    <Clock className="w-5 h-5 text-white" />
                  ) : (
                    <span className="text-sm font-medium text-[var(--color-text-muted)]">
                      {index + 1}
                    </span>
                  )}
                </div>

                {/* Step content */}
                <div className="flex-1 pt-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3
                      className={`font-semibold ${
                        step.completed || step.active
                          ? 'text-[var(--color-text-primary)]'
                          : 'text-[var(--color-text-muted)]'
                      }`}
                      style={{ fontFamily: 'Outfit, system-ui, sans-serif' }}
                    >
                      {step.label}
                    </h3>
                    {step.completed && (
                      <StatusBadge text="Done" variant="done" size="sm" />
                    )}
                    {step.active && !step.completed && (
                      <StatusBadge text="Active" variant="active" size="sm" />
                    )}
                  </div>
                  <p
                    className={`text-sm ${
                      step.completed || step.active
                        ? 'text-[var(--color-text-secondary)]'
                        : 'text-[var(--color-text-muted)]'
                    }`}
                  >
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-[var(--color-bg-elevated)] border border-[var(--color-border)] rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-[var(--color-needs-human-tint)] border border-[var(--color-needs-human-tint-bd)] rounded-lg">
            <FileText className="w-6 h-6 text-[var(--color-brand)]" />
          </div>
          <div>
            <h3
              className="font-semibold text-[var(--color-text-primary)] mb-1"
              style={{ fontFamily: 'Outfit, system-ui, sans-serif' }}
            >
              What happens next?
            </h3>
            <p className="text-sm text-[var(--color-text-secondary)]">
              Once all documents are uploaded and transactions are categorized, our forensic
              accounting team will begin analyzing your case. You&apos;ll be notified when the
              analysis is complete and your case is ready for review.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
